import { userTier } from '@apollo-internal/cache';
import { useQuery, useReactiveVar } from '@apollo/client';
import PageInstructions from '@shared/components/PageInstructions';
import ApolloError from '@shared/components/ApolloError';
import React, { useMemo } from 'react';
import { PAGE_MODE } from '../../constants/pageModeTypes';
import GET_DEACTIVATED_USERS from '../../queries/changeLicenseQueries/getDeactivatedUsers';
import LicensesTable from '../shared/components/LicensesTable';
import { getAssignedUserLicenseInformation } from '../shared/services/userLicenseInformationService';
import { Navigate } from 'react-router-dom';
import { getDataNotFoundPage } from '@shared/services/getDataNotFoundPageService';

const PAGE_INSTRUCTION_TITLE = 'User Management - Deactivated Licenses Instructions';

/**
 * @typedef {object} UserDeactivatedLicensesTableProps
 * @property {number} pageMode
 * @property {number} [orgId]
 * @property {string} [orgName]
 * @property {number} [orgUnitId]
 */

/**
 * @param {UserDeactivatedLicensesTableProps} props
 */
const UserDeactivatedLicensesTable = props => {
  const { pageMode, orgId = null, orgName = '', orgUnitId = null } = props;

  const currentUserTier = useReactiveVar(userTier);

  const description = getPageDescription(pageMode);
  const displayOrgUnitTable = pageMode === PAGE_MODE.MddapOrg || pageMode === PAGE_MODE.ISACAVipOrg;

  const { data, error, loading } = useQuery(GET_DEACTIVATED_USERS, {
    variables: orgUnitId ? { orgUnitId } : { organizationId: orgId },
    notifyOnNetworkStatusChange: true
  });

  const userLicenseInformation = useMemo(() => {
    if (!data?.deactivatedUsers) return null;

    const { OrgUsers, OrgUnitUsers } = data.deactivatedUsers;
    const users = OrgUsers || OrgUnitUsers;

    return getAssignedUserLicenseInformation(
      users,
      displayOrgUnitTable && OrgUnitUsers,
      pageMode,
      currentUserTier,
      false
    );
  }, [data, displayOrgUnitTable, pageMode, currentUserTier]);

  if (error) {
    return <ApolloError />;
  }

  if (data && data.deactivatedUsers === null) {
    return <Navigate to={getDataNotFoundPage(currentUserTier)} />;
  }

  return (
    <div className='user-table-of-licenses'>
      <div className='page-header'>
        <PageInstructions title={PAGE_INSTRUCTION_TITLE} description={description} />
      </div>

      <LicensesTable
        usersData={userLicenseInformation?.users}
        orgId={orgId}
        orgName={orgName}
        pageMode={pageMode}
        loading={loading}
      />

      {displayOrgUnitTable && (
        <LicensesTable
          usersData={userLicenseInformation?.organizationOrgUnitUsers}
          orgId={orgId}
          orgName={orgName}
          pageMode={
            pageMode === PAGE_MODE.ISACAVipOrg ? PAGE_MODE.ISACAAppraiser : PAGE_MODE.OrgUnit
          }
          singleUnit={true}
          loading={loading}
        />
      )}
    </div>
  );
};

const getPageDescription = pageMode => {
  if (pageMode !== PAGE_MODE.MddapOrg) {
    return (
      'The table below displays all deactivated licenses for your organization. ' +
      'The table can be sorted or filtered depending on the column. ' +
      'Licenses can be reactivated on the Edit License page.'
    );
  }

  return (
    'The tables below display all deactivated licenses. ' +
    'The first table lists all deactivated organization licenses and the second table ' +
    'lists all deactivated organizational unit licenses. ' +
    'Both tables can be sorted or filtered depending on the column. ' +
    'Licenses can be reactivated on the Edit License page.'
  );
};

export default UserDeactivatedLicensesTable;
